import { useInView } from "@/hooks/useInView";

const AboutSection = () => {
  const { ref: headerRef, isInView: headerInView } = useInView({ threshold: 0.2 });
  const { ref: cardsRef, isInView: cardsInView } = useInView({ threshold: 0.1 });
  const { ref: whyChooseRef, isInView: whyChooseInView } = useInView({ threshold: 0.1 });

  return (
    <section id="about" className="section-padding bg-gradient-subtle">
      <div className="container-narrow">
        <div
          ref={headerRef}
          className={`text-center space-y-6 mb-16 transition-all duration-1000 ${
            headerInView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl sm:text-5xl font-bold font-heading text-foreground">
            About <span className="text-primary">Revive Meal</span> Ethiopia
          </h2>
          <p className="text-xl text-muted-foreground font-body leading-relaxed max-w-3xl mx-auto">
            We believe that healthy eating should be accessible, delicious, and
            convenient. Our mission is to revive Ethiopia's wellness culture
            through carefully crafted meal preparations.
          </p>
        </div>

        <div
          ref={cardsRef}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <div
            className={`bg-white rounded-2xl p-8 shadow-soft card-hover text-center space-y-4 transition-all duration-1000 delay-100 ${
              cardsInView
                ? "opacity-100 translate-x-0 scale-100"
                : "opacity-0 -translate-x-20 scale-95"
            }`}
          >
            <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto">
              <span className="text-3xl">🌱</span>
            </div>
            <h3 className="text-2xl font-semibold font-heading text-foreground">
              Fresh & Local
            </h3>
            <p className="text-muted-foreground font-body">
              We source the freshest ingredients from local Ethiopian farmers,
              supporting our community while ensuring premium quality.
            </p>
          </div>

          <div
            className={`bg-white rounded-2xl p-8 shadow-soft card-hover text-center space-y-4 transition-all duration-1000 delay-300 ${
              cardsInView
                ? "opacity-100 translate-y-0 scale-100"
                : "opacity-0 translate-y-20 scale-90"
            }`}
          >
            <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto">
              <span className="text-3xl">👨‍🍳</span>
            </div>
            <h3 className="text-2xl font-semibold font-heading text-foreground">
              Expert Nutrition
            </h3>
            <p className="text-muted-foreground font-body">
              Our meals are designed by certified nutritionists and prepared by
              experienced chefs who understand Ethiopian culinary traditions.
            </p>
          </div>

          <div
            className={`bg-white rounded-2xl p-8 shadow-soft card-hover text-center space-y-4 transition-all duration-1000 delay-500 ${
              cardsInView
                ? "opacity-100 translate-x-0 scale-100"
                : "opacity-0 translate-x-20 scale-95"
            }`}
          >
            <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto">
              <span className="text-3xl">🎯</span>
            </div>
            <h3 className="text-2xl font-semibold font-heading text-foreground">
              Personalized
            </h3>
            <p className="text-muted-foreground font-body">
              Every meal plan is customized to your dietary preferences, health
              goals, and lifestyle needs for optimal results.
            </p>
          </div>
        </div>

        <div
          ref={whyChooseRef}
          className={`mt-16 bg-white rounded-3xl p-8 sm:p-12 shadow-soft transition-all duration-1000 ${
            whyChooseInView
              ? "opacity-100 scale-100 rotate-0"
              : "opacity-0 scale-95 -rotate-1"
          }`}
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <h3 className="text-3xl font-bold font-heading text-foreground">
                Why Choose Revive Meal Ethiopia?
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Time-Saving Convenience
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      No more meal planning, shopping, or cooking. We handle
                      everything.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Sustainable Weight Management
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Balanced portions and nutrients to support your health
                      journey.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Cultural Heritage
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Authentic Ethiopian flavors reimagined for modern healthy
                      living.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Flexible Plans
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Choose from various meal plans that fit your budget and
                      lifestyle.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-primary rounded-2xl p-8 text-white text-center space-y-4">
                <h4 className="text-2xl font-bold font-heading">
                  Ready to Transform?
                </h4>
                <p className="font-body opacity-90">
                  Join hundreds of Ethiopians who have already started their
                  health journey with us.
                </p>
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div>
                    <p className="text-3xl font-bold">95%</p>
                    <p className="text-sm opacity-90">Customer Satisfaction</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold">4.8★</p>
                    <p className="text-sm opacity-90">Average Rating</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
